<template>
  <view
    v-if="visible"
    class="trade-menu-overlay"
    @click="handleClose"
    @touchmove.stop.prevent
  >
    <view class="menu-container" @click.stop @touchmove.stop>
      <view class="menu-list">
        <view
          v-for="item in menuItems"
          :key="item.key"
          class="menu-item"
          :class="{ active: item.key === currentType }"
          @click="handleSelect(item)"
        >
          <view class="menu-icon-wrap">
            <image class="menu-icon" :src="item.icon" mode="aspectFit" :lazy-load="false" />
          </view>
          <text class="menu-text">{{ item.text }}</text>
          <view v-if="item.key === currentType" class="check-icon">
            <decision-icon type="yes" color="#ffffff" :size="22" />
          </view>
        </view>
      </view>
      <!-- 底部凹槽切图，嵌住中间 FAB -->
      <image
        src="/static/image/tabbar/bg.webp"
        class="action-icon"
        mode="widthFix"
        :lazy-load="false"
      />
    </view>
  </view>
</template>

<script setup lang="ts">
import { computed, toRef, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import DecisionIcon from '@/components/decision-icon/index.vue';
import { useScrollLock } from '@/composables/useScrollLock';
import { preloadChromeImages } from '@/common/utils/preloadChromeImages';

const props = defineProps<{
  visible: boolean;
  currentType?: string;
}>();

const visibleRef = toRef(props, 'visible');
useScrollLock(visibleRef);

/** 打开快捷菜单前再预热一次图标（进页 XHR 多时仍可能未命中缓存） */
watch(
  () => props.visible,
  (v) => {
    if (v) preloadChromeImages(true);
  },
  { immediate: true }
);

const emit = defineEmits<{
  (e: 'close'): void;
  (e: 'select', item: { key: string; route: string }): void;
}>();

const { t } = useI18n();

/** 快捷入口：AI 预测 / 跟单 / 创建市场（图标统一黑色扁平剪影） */
const menuItems = computed(() => [
  {
    key: 'prediction',
    text: t('tabbar.prediction'),
    icon: '/static/image/tabbar/t_ai.webp',
    route: '/pages/aiprediction/index',
  },
  {
    key: 'copyTrade',
    text: t('copyTrade.menuTitle'),
    icon: '/static/image/tabbar/t_contract.webp',
    route: '/pages/copy-trade/index',
  },
  {
    key: 'createMarket',
    text: t('tabbar.createMarket'),
    icon: '/static/image/tabbar/t_jiedian.webp',
    route: '/pages/create-market/index',
  },
]);

const handleClose = () => {
  emit('close');
};

const handleSelect = (item: { key: string; route: string }) => {
  emit('select', item);
};
</script>

<style lang="scss" scoped>
.trade-menu-overlay {
  position: fixed;
  top: 0;
  bottom: 0;
  /* 宽屏与内容柱对齐（同 sidebar），遮罩不铺到两侧灰边 */
  left: max(0px, calc((100vw - min(100vw, var(--app-max-width, 750px))) / 2));
  width: min(100vw, var(--app-max-width, 750px));
  background-color: rgba(0, 0, 0, 0.45);
  /* 低于底栏，底栏仍盖在上面可点关闭 */
  z-index: 10020;
}

.menu-container {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  /* 与 custom-tabbar 同宽：min(48rpx,48px) 避免宽屏 rpx 放大把边距撑开 */
  width: calc(100% - min(48rpx, 48px));
  max-width: calc(var(--app-max-width, 750px) - 48px);
  /*
   * 底栏整体高 180rpx（bottom:10px），FAB 上探 28px。
   * 白卡片底边需高于 FAB 顶，凹槽切图再向下嵌住关闭钮，避免白底盖住 X。
   */
  bottom: calc(10px + 180rpx + 40px + env(safe-area-inset-bottom));
  background-color: #ffffff;
  border-radius: 28rpx 28rpx 0 0;
  box-shadow: 0 0 34rpx rgba(17, 24, 37, 0.14);
  padding: 28rpx 28rpx 0;
  z-index: 1;
  box-sizing: border-box;
  overflow: visible;

  .action-icon {
    width: 100%;
    height: auto;
    position: absolute;
    left: 0;
    top: 100%;
    bottom: auto;
    transform: translateY(-2rpx);
    display: block;
    pointer-events: none;
    z-index: 0;
  }
}

.menu-list {
  position: relative;
  z-index: 2;
  padding-bottom: 8rpx;
}

.menu-item {
  display: flex;
  align-items: center;
  padding: 0 20rpx 0 12rpx;
  height: 96rpx;
  border-radius: 20rpx;
  margin-bottom: 12rpx;
  background-color: transparent;
  transition: background-color 0.15s ease;

  /* 选中态：浅灰底 + 右侧勾 */
  &.active {
    background-color: #f4f6f8;
  }

  &:active {
    background-color: #f0f2f5;
  }

  &:last-child {
    margin-bottom: 0;
  }
}

.menu-icon-wrap {
  width: 64rpx;
  height: 64rpx;
  margin-right: 20rpx;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.menu-icon {
  width: 40rpx;
  height: 40rpx;
  display: block;
  /* 统一黑色扁平剪影，与跟单 / 创建市场一致 */
  filter: brightness(0);
}

.menu-text {
  flex: 1;
  font-weight: 600;
  font-size: 28rpx;
  color: #171b24;
}

.check-icon {
  width: 36rpx;
  height: 36rpx;
  border-radius: 50%;
  background-color: #171b24;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
</style>
